import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button, VStack, HStack, Text, Box, Checkbox, useToast,
  Table, Thead, Tbody, Tr, Th, Td, Badge, Divider,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useDisclosure,
} from '@chakra-ui/react';
import { FiDownload, FiLock } from 'react-icons/fi';
import axiosInstance from '../../../api/AxiosInstance';
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  taller: any;
  onGuardado: () => void;
}

const ModalAsistencia: React.FC<Props> = ({ isOpen, onClose, taller, onGuardado }) => {
  const toast = useToast();
  const cancelRef = useRef<any>(null);
  const { isOpen: isOpenCerrar, onOpen: onOpenCerrar, onClose: onCloseCerrar } = useDisclosure();

  const [inscripciones, setInscripciones] = useState<any[]>([]);
  const [asistencia, setAsistencia] = useState<Record<number, boolean>>({});
  const [guardando, setGuardando] = useState(false);
  const [cerrando, setCerrando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(`/cultura/talleres/${taller.id}/inscripciones`);
      setInscripciones(data);
      const mapa: Record<number, boolean> = {};
      data.forEach((i: any) => { mapa[i.id] = i.asistio; });
      setAsistencia(mapa);
    } catch {}
  }, [taller.id]);

  useEffect(() => { if (isOpen) cargar(); }, [isOpen, cargar]);

  const toggleAsistencia = (id: number) =>
    setAsistencia((p) => ({ ...p, [id]: !p[id] }));

  const handleGuardar = async () => {
    setGuardando(true);
    try {
      const payload = inscripciones.map((i) => ({ id: i.id, asistio: asistencia[i.id] ?? false }));
      await axiosInstance.put(`/cultura/talleres/${taller.id}/asistencia`, { inscripciones: payload });
      toast({ title: 'Asistencia guardada', status: 'success', duration: 2000 });
      onGuardado();
    } catch (e: any) {
      toast({ title: 'Error', description: e?.response?.data?.message, status: 'error', duration: 3000 });
    } finally {
      setGuardando(false);
    }
  };

  const handleCerrar = async () => {
    setCerrando(true);
    try {
      await axiosInstance.put(`/cultura/talleres/${taller.id}/cerrar`);
      toast({ title: 'Taller marcado como Concluido', status: 'success', duration: 3000 });
      onCloseCerrar();
      onGuardado();
      onClose();
    } catch (e: any) {
      toast({ title: 'Error', description: e?.response?.data?.message, status: 'error', duration: 3000 });
    } finally {
      setCerrando(false);
    }
  };

  const exportarPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text(`Lista de Asistencia: ${taller.titulo}`, 14, 16);
    const asistieron = Object.values(asistencia).filter(Boolean).length;
    doc.setFontSize(10);
    doc.text(`Inscritos: ${inscripciones.length}   |   Asistieron: ${asistieron}`, 14, 24);
    autoTable(doc, {
      startY: 30,
      head: [['#', 'Nombre', 'Apellidos', 'Asistió']],
      body: inscripciones.map((i, idx) => [
        idx + 1, i.nombre, i.apellidos, asistencia[i.id] ? 'Sí' : 'No',
      ]),
      styles: { fontSize: 9 },
    });
    doc.save(`Asistencia_${taller.titulo}.pdf`);
  };

  const totalAsistieron = Object.values(asistencia).filter(Boolean).length;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">
            Asistencia — {taller.titulo}
          </ModalHeader>
          <ModalBody py={4}>
            <VStack spacing={4} align="stretch">
              <HStack spacing={3}>
                <Badge colorScheme="blue" px={3} py={1} borderRadius="full">
                  Inscritos: {inscripciones.length}
                </Badge>
                <Badge colorScheme="green" px={3} py={1} borderRadius="full">
                  Asistieron: {totalAsistieron}
                </Badge>
                <Badge colorScheme={taller.estatus === 'Concluido' ? 'purple' : 'gray'} px={3} py={1} borderRadius="full">
                  {taller.estatus}
                </Badge>
              </HStack>

              {inscripciones.length === 0 ? (
                <Text color="gray.400" textAlign="center" py={4}>Sin participantes inscritos</Text>
              ) : (
                <Box overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th>#</Th><Th>Nombre</Th><Th>Apellidos</Th>
                        <Th>Localidad</Th><Th textAlign="center">Asistió</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {inscripciones.map((i, idx) => (
                        <Tr key={i.id} bg={asistencia[i.id] ? 'green.50' : undefined}>
                          <Td>{idx + 1}</Td>
                          <Td>{i.nombre}</Td>
                          <Td>{i.apellidos}</Td>
                          <Td>{i.localidad || '-'}</Td>
                          <Td textAlign="center">
                            <Checkbox
                              isChecked={asistencia[i.id] ?? false}
                              onChange={() => toggleAsistencia(i.id)}
                              colorScheme="green"
                              isDisabled={taller.estatus === 'Concluido'}
                            />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}

              <Divider />

              {taller.estatus !== 'Concluido' && (
                <Box border="1px dashed" borderColor="red.200" borderRadius="md" p={3} bg="red.50">
                  <Text fontSize="sm" color="red.700" mb={2}>
                    Al marcar como <strong>Concluido</strong> el taller quedará cerrado para nuevas inscripciones.
                  </Text>
                  <Button size="sm" leftIcon={<FiLock />} colorScheme="red" variant="outline" onClick={onOpenCerrar}>
                    Marcar como Concluido
                  </Button>
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button size="sm" leftIcon={<FiDownload />} variant="outline" onClick={exportarPDF}>
              Descargar PDF
            </Button>
            <Button variant="outline" onClick={onClose} isDisabled={guardando}>Cerrar</Button>
            {taller.estatus !== 'Concluido' && (
              <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleGuardar}
                isLoading={guardando} loadingText="Guardando...">
                Guardar asistencia
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={isOpenCerrar} leastDestructiveRef={cancelRef} onClose={onCloseCerrar}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader color="#5b182b">Cerrar taller</AlertDialogHeader>
            <AlertDialogBody>
              ¿Confirmas que el taller <strong>{taller.titulo}</strong> ha concluido?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onCloseCerrar} isDisabled={cerrando}>Cancelar</Button>
              <Button colorScheme="red" onClick={handleCerrar} ml={3} isLoading={cerrando} loadingText="Cerrando...">
                Sí, concluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default ModalAsistencia;
